// 051 parte (c): bloque GESTACIÓN ACTUAL (form. 051 mayo/2016). Sí / No / sin responder con `SiNoBotones`; las casillas de más de dos
// círculos del papel (n/c, s/d, "no se hizo"...) son una elección única (`EleccionBotones`, volver a pulsar limpia).
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { CampoDictado } from './CampoDictado';
import { SiNoBotones } from './camposSimples';
import { leerSiNo, type SiNoValor } from './camposSimplesDatos';
import {
  ANTIRRUBEOLA_OPCIONES,
  BACTERIURIA_OPCIONES,
  CERVIX_EXAMENES_OPCIONES,
  CERVIX_RESULTADO_OPCIONES,
  CHAGAS_PALUDISMO_OPCIONES,
  ESTREPTOCOCO_B_OPCIONES,
  FACTORES_RIESGO_051,
  RH_OPCIONES,
  SESIONES_EDUCACION_PRENATAL_051,
  SI_NO_NC_OPCIONES,
  SIFILIS_NO_TREPONEMICA_OPCIONES,
  SIFILIS_PAREJA_OPCIONES,
  SIFILIS_TREPONEMICA_OPCIONES,
  TOXOPLASMOSIS_OPCIONES,
  TRIMESTRES_051,
  VIH_RESULTADO_OPCIONES,
  claveFactor051,
  sifilisTieneSemanas051,
  vihTieneResultado051,
  type EstadoGestacionActual051,
  type EstadoGlucemia051,
  type EstadoHb051,
  type EstadoSifilisFila051,
  type EstadoVih051,
} from './bloques051GestacionDatos';

const P = 'formularios.f051.gestacionActual';

function CampoTexto({ id, label, value, onChange }: { readonly id: string; readonly label: string; readonly value: string; readonly onChange: (v: string) => void }): JSX.Element {
  return <CampoDictado id={id} etiqueta={label} valor={value} onChange={onChange} />;
}

function CampoCheckbox({ id, label, checked, onChange }: { readonly id: string; readonly label: string; readonly checked: boolean; readonly onChange: (v: boolean) => void }): JSX.Element {
  return (
    <div className="flex items-center gap-2">
      <Checkbox id={id} checked={checked} onCheckedChange={(v) => onChange(v === true)} />
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
    </div>
  );
}

/** Elección única entre los círculos del papel: pulsar la opción activa la deja sin responder. */
function EleccionBotones<T extends string>({
  id,
  etiqueta,
  opciones,
  valor,
  onChange,
}: {
  readonly id: string;
  readonly etiqueta: string;
  readonly opciones: readonly T[];
  readonly valor: T | '';
  readonly onChange: (v: T | '') => void;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <fieldset id={id} className="flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend className="p-0 text-sm font-medium">{etiqueta}</legend>
      <div className="flex flex-wrap gap-2">
        {opciones.map((o) => (
          <Button key={o} type="button" size="sm" variant={valor === o ? 'default' : 'outline'} aria-pressed={valor === o} onClick={() => onChange(valor === o ? '' : o)}>
            {t(`${P}.opcion.${o}`)}
          </Button>
        ))}
      </div>
    </fieldset>
  );
}

function SiNo({ id, etiqueta, valor, onChange }: { readonly id: string; readonly etiqueta: string; readonly valor: SiNoValor; readonly onChange: (v: SiNoValor) => void }): JSX.Element {
  const { t } = useTranslation();
  return <SiNoBotones id={id} etiqueta={etiqueta} valor={valor} textos={{ si: t('formulario.si'), no: t('formulario.no') }} onChange={onChange} />;
}

const REJILLA = 'grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3';

function Subtitulo({ children }: { readonly children: string }): JSX.Element {
  return <h5 className="text-sm font-medium text-hce-ink">{children}</h5>;
}

function BloqueVih({ grupo, estado, onChange }: { readonly grupo: 'menor20' | 'mayorIgual20'; readonly estado: EstadoVih051; readonly onChange: (v: EstadoVih051) => void }): JSX.Element {
  const { t } = useTranslation();
  const id = `f051-vih-${grupo}`;
  return (
    <div className="flex flex-col gap-2">
      <Subtitulo>{`${t(`${P}.vih.titulo`)} — ${t(`${P}.grupos.${grupo}`)}`}</Subtitulo>
      <div className={REJILLA}>
        <EleccionBotones id={`${id}-solicitada`} etiqueta={t(`${P}.vih.solicitada`)} opciones={SI_NO_NC_OPCIONES} valor={estado.solicitada} onChange={(v) => onChange({ ...estado, solicitada: v })} />
        {vihTieneResultado051(estado) && (
          <EleccionBotones id={`${id}-resultado`} etiqueta={t(`${P}.vih.resultado`)} opciones={VIH_RESULTADO_OPCIONES} valor={estado.resultado} onChange={(v) => onChange({ ...estado, resultado: v })} />
        )}
        <EleccionBotones id={`${id}-tarv`} etiqueta={t(`${P}.vih.tarv`)} opciones={SI_NO_NC_OPCIONES} valor={estado.tarv} onChange={(v) => onChange({ ...estado, tarv: v })} />
      </div>
    </div>
  );
}

function BloqueHb({ id, etiqueta, estado, onChange }: { readonly id: string; readonly etiqueta: string; readonly estado: EstadoHb051; readonly onChange: (v: EstadoHb051) => void }): JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <CampoTexto id={id} label={etiqueta} value={estado.valor} onChange={(v) => onChange({ ...estado, valor: v })} />
      <CampoCheckbox id={`${id}-menor11`} label={t(`${P}.hbMenor11`)} checked={estado.menor11} onChange={(v) => onChange({ ...estado, menor11: v })} />
    </div>
  );
}

function BloqueGlucemia({ grupo, estado, onChange }: { readonly grupo: 'menor20' | 'mayor30'; readonly estado: EstadoGlucemia051; readonly onChange: (v: EstadoGlucemia051) => void }): JSX.Element {
  const { t } = useTranslation();
  const id = `f051-glucemia-${grupo}`;
  return (
    <div className="flex flex-col gap-2">
      <CampoTexto id={id} label={`${t(`${P}.glucemia.titulo`)} ${t(`${P}.glucemia.${grupo}`)}`} value={estado.valor} onChange={(v) => onChange({ ...estado, valor: v })} />
      <CampoCheckbox id={`${id}-92`} label={t(`${P}.glucemia.mayorIgual92`)} checked={estado.mayorIgual92} onChange={(v) => onChange({ ...estado, mayorIgual92: v })} />
    </div>
  );
}

function FilaSifilis({ grupo, estado, onChange }: { readonly grupo: 'menor20' | 'mayorIgual20'; readonly estado: EstadoSifilisFila051; readonly onChange: (v: EstadoSifilisFila051) => void }): JSX.Element {
  const { t } = useTranslation();
  const id = `f051-sifilis-${grupo}`;
  const s = `${P}.sifilis`;
  return (
    <div className="flex flex-col gap-2">
      <Subtitulo>{`${t(`${s}.titulo`)} — ${t(`${P}.grupos.${grupo}`)}`}</Subtitulo>
      <div className={REJILLA}>
        <EleccionBotones id={`${id}-notrep`} etiqueta={t(`${s}.noTreponemica`)} opciones={SIFILIS_NO_TREPONEMICA_OPCIONES} valor={estado.noTreponemica} onChange={(v) => onChange({ ...estado, noTreponemica: v })} />
        {sifilisTieneSemanas051(estado.noTreponemica) && (
          <CampoTexto id={`${id}-notrep-semanas`} label={`${t(`${s}.noTreponemica`)} (${t(`${s}.semanas`)})`} value={estado.noTreponemicaSemanas} onChange={(v) => onChange({ ...estado, noTreponemicaSemanas: v })} />
        )}
        <EleccionBotones id={`${id}-trep`} etiqueta={t(`${s}.treponemica`)} opciones={SIFILIS_TREPONEMICA_OPCIONES} valor={estado.treponemica} onChange={(v) => onChange({ ...estado, treponemica: v })} />
        {sifilisTieneSemanas051(estado.treponemica) && (
          <CampoTexto id={`${id}-trep-semanas`} label={`${t(`${s}.treponemica`)} (${t(`${s}.semanas`)})`} value={estado.treponemicaSemanas} onChange={(v) => onChange({ ...estado, treponemicaSemanas: v })} />
        )}
        <EleccionBotones id={`${id}-trat`} etiqueta={t(`${s}.tratamiento`)} opciones={SIFILIS_TREPONEMICA_OPCIONES} valor={estado.tratamiento} onChange={(v) => onChange({ ...estado, tratamiento: v })} />
        {sifilisTieneSemanas051(estado.tratamiento) && (
          <CampoTexto id={`${id}-trat-semanas`} label={`${t(`${s}.tratamiento`)} (${t(`${s}.semanas`)})`} value={estado.tratamientoSemanas} onChange={(v) => onChange({ ...estado, tratamientoSemanas: v })} />
        )}
        <EleccionBotones id={`${id}-pareja`} etiqueta={t(`${s}.tratamientoPareja`)} opciones={SIFILIS_PAREJA_OPCIONES} valor={estado.tratamientoPareja} onChange={(v) => onChange({ ...estado, tratamientoPareja: v })} />
      </div>
    </div>
  );
}

export function BloqueGestacionActual({ estado, onChange }: { readonly estado: EstadoGestacionActual051; readonly onChange: (v: EstadoGestacionActual051) => void }): JSX.Element {
  const { t } = useTranslation();
  function actualizar(cambios: Partial<EstadoGestacionActual051>): void {
    onChange({ ...estado, ...cambios });
  }
  return (
    <div className="flex flex-col gap-4">
      <h4 className="text-sm font-medium text-hce-ink">{t(`${P}.titulo`)}</h4>

      <div className={REJILLA}>
        <CampoTexto id="f051-peso-inicial" label={t(`${P}.pesoInicial`)} value={estado.pesoInicial} onChange={(v) => actualizar({ pesoInicial: v })} />
        <CampoTexto id="f051-talla" label={t(`${P}.talla`)} value={estado.talla} onChange={(v) => actualizar({ talla: v })} />
        <SiNo id="f051-eg-fum" etiqueta={t(`${P}.egConfiableFum`)} valor={estado.egConfiableFum} onChange={(v) => actualizar({ egConfiableFum: v })} />
        <SiNo id="f051-eg-eco" etiqueta={t(`${P}.egConfiableEco`)} valor={estado.egConfiableEco} onChange={(v) => actualizar({ egConfiableEco: v })} />
        <SiNo id="f051-eco-11-13" etiqueta={t(`${P}.ecografia11a13Semanas`)} valor={estado.ecografia11a13Semanas} onChange={(v) => actualizar({ ecografia11a13Semanas: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <Subtitulo>{t(`${P}.factoresTitulo`)}</Subtitulo>
        <div className={REJILLA}>
          {FACTORES_RIESGO_051.flatMap((f) =>
            TRIMESTRES_051.map((tri) => {
              const clave = claveFactor051(f, tri);
              return (
                <SiNo
                  key={clave}
                  id={`f051-factor-${f}-${tri}`}
                  etiqueta={`${t(`${P}.factores.${f}`)} — ${t(`${P}.trimestres.${tri}`)}`}
                  valor={leerSiNo(estado.factores[clave])}
                  onChange={(v) => actualizar({ factores: { ...estado.factores, [clave]: v } })}
                />
              );
            })
          )}
        </div>
      </div>

      <div className={REJILLA}>
        <EleccionBotones id="f051-antirrubeola" etiqueta={t(`${P}.antirrubeola`)} opciones={ANTIRRUBEOLA_OPCIONES} valor={estado.antirrubeola} onChange={(v) => actualizar({ antirrubeola: v })} />
        <SiNo id="f051-antitetanica-vigente" etiqueta={t(`${P}.antitetanicaVigente`)} valor={estado.antitetanicaVigente} onChange={(v) => actualizar({ antitetanicaVigente: v })} />
        <CampoTexto id="f051-antitetanica-dosis1" label={t(`${P}.antitetanicaDosis1`)} value={estado.antitetanicaDosis1} onChange={(v) => actualizar({ antitetanicaDosis1: v })} />
        <CampoTexto id="f051-antitetanica-dosis2" label={t(`${P}.antitetanicaDosis2`)} value={estado.antitetanicaDosis2} onChange={(v) => actualizar({ antitetanicaDosis2: v })} />
        <SiNo id="f051-examen-mamas" etiqueta={t(`${P}.examenNormalMamas`)} valor={estado.examenNormalMamas} onChange={(v) => actualizar({ examenNormalMamas: v })} />
        <SiNo id="f051-examen-odontologico" etiqueta={t(`${P}.examenNormalOdontologico`)} valor={estado.examenNormalOdontologico} onChange={(v) => actualizar({ examenNormalOdontologico: v })} />
      </div>

      <div className="flex flex-col gap-2">
        <Subtitulo>{t(`${P}.cervix.titulo`)}</Subtitulo>
        <div className={REJILLA}>
          {CERVIX_EXAMENES_OPCIONES.map((c) => (
            <EleccionBotones
              key={c}
              id={`f051-cervix-${c}`}
              etiqueta={t(`${P}.cervix.${c}`)}
              opciones={CERVIX_RESULTADO_OPCIONES}
              valor={estado.cervix[c] ?? ''}
              onChange={(v) => actualizar({ cervix: { ...estado.cervix, [c]: v } })}
            />
          ))}
        </div>
      </div>

      <div className={REJILLA}>
        <CampoTexto id="f051-grupo" label={t(`${P}.grupo`)} value={estado.grupo} onChange={(v) => actualizar({ grupo: v })} />
        <EleccionBotones id="f051-rh" etiqueta={t(`${P}.rh`)} opciones={RH_OPCIONES} valor={estado.rh} onChange={(v) => actualizar({ rh: v })} />
        <SiNo id="f051-inmunizacion" etiqueta={t(`${P}.inmunizacion`)} valor={estado.inmunizacion} onChange={(v) => actualizar({ inmunizacion: v })} />
        <EleccionBotones id="f051-toxoplasmosis" etiqueta={t(`${P}.toxoplasmosis`)} opciones={TOXOPLASMOSIS_OPCIONES} valor={estado.toxoplasmosis} onChange={(v) => actualizar({ toxoplasmosis: v })} />
      </div>

      <BloqueVih grupo="menor20" estado={estado.vihMenor20} onChange={(v) => actualizar({ vihMenor20: v })} />
      <BloqueVih grupo="mayorIgual20" estado={estado.vihMayorIgual20} onChange={(v) => actualizar({ vihMayorIgual20: v })} />

      <div className={REJILLA}>
        <BloqueHb id="f051-hb-menor20" etiqueta={t(`${P}.hbMenor20`)} estado={estado.hbMenor20} onChange={(v) => actualizar({ hbMenor20: v })} />
        <SiNo id="f051-fe" etiqueta={t(`${P}.feIndicado`)} valor={estado.feIndicado} onChange={(v) => actualizar({ feIndicado: v })} />
        <SiNo id="f051-folatos" etiqueta={t(`${P}.folatosIndicados`)} valor={estado.folatosIndicados} onChange={(v) => actualizar({ folatosIndicados: v })} />
        <BloqueHb id="f051-hb-mayor20" etiqueta={t(`${P}.hbMayorIgual20`)} estado={estado.hbMayorIgual20} onChange={(v) => actualizar({ hbMayorIgual20: v })} />
      </div>

      <FilaSifilis grupo="menor20" estado={estado.sifilisMenor20} onChange={(v) => actualizar({ sifilisMenor20: v })} />
      <FilaSifilis grupo="mayorIgual20" estado={estado.sifilisMayorIgual20} onChange={(v) => actualizar({ sifilisMayorIgual20: v })} />

      <div className={REJILLA}>
        <EleccionBotones id="f051-chagas" etiqueta={t(`${P}.chagas`)} opciones={CHAGAS_PALUDISMO_OPCIONES} valor={estado.chagas} onChange={(v) => actualizar({ chagas: v })} />
        <EleccionBotones id="f051-paludismo" etiqueta={t(`${P}.paludismoMalaria`)} opciones={CHAGAS_PALUDISMO_OPCIONES} valor={estado.paludismoMalaria} onChange={(v) => actualizar({ paludismoMalaria: v })} />
        <EleccionBotones
          id="f051-bacteriuria-menor20"
          etiqueta={`${t(`${P}.bacteriuria`)} ${t(`${P}.grupos.menor20`)}`}
          opciones={BACTERIURIA_OPCIONES}
          valor={estado.bacteriuriaMenor20}
          onChange={(v) => actualizar({ bacteriuriaMenor20: v })}
        />
        <EleccionBotones
          id="f051-bacteriuria-mayor20"
          etiqueta={`${t(`${P}.bacteriuria`)} ${t(`${P}.grupos.mayorIgual20`)}`}
          opciones={BACTERIURIA_OPCIONES}
          valor={estado.bacteriuriaMayorIgual20}
          onChange={(v) => actualizar({ bacteriuriaMayorIgual20: v })}
        />
        <BloqueGlucemia grupo="menor20" estado={estado.glucemiaMenor20} onChange={(v) => actualizar({ glucemiaMenor20: v })} />
        <BloqueGlucemia grupo="mayor30" estado={estado.glucemiaMayor30} onChange={(v) => actualizar({ glucemiaMayor30: v })} />
        <SiNo id="f051-calcio" etiqueta={t(`${P}.calcio`)} valor={estado.calcio} onChange={(v) => actualizar({ calcio: v })} />
        <EleccionBotones id="f051-aas" etiqueta={t(`${P}.acidoAcetilsalicilico`)} opciones={SI_NO_NC_OPCIONES} valor={estado.acidoAcetilsalicilico} onChange={(v) => actualizar({ acidoAcetilsalicilico: v })} />
        <EleccionBotones id="f051-estreptococo-b" etiqueta={t(`${P}.estreptococoB`)} opciones={ESTREPTOCOCO_B_OPCIONES} valor={estado.estreptococoB} onChange={(v) => actualizar({ estreptococoB: v })} />
        <SiNo id="f051-plan-parto" etiqueta={t(`${P}.planPartoEmergencia`)} valor={estado.planPartoEmergencia} onChange={(v) => actualizar({ planPartoEmergencia: v })} />
        <SiNo id="f051-consejeria-lactancia" etiqueta={t(`${P}.consejeriaLactanciaMaterna`)} valor={estado.consejeriaLactanciaMaterna} onChange={(v) => actualizar({ consejeriaLactanciaMaterna: v })} />
      </div>

      <div className="flex flex-col gap-1">
        <Label>{t(`${P}.educacionPrenatal`)}</Label>
        <div className="flex flex-wrap gap-x-4 gap-y-2">
          {SESIONES_EDUCACION_PRENATAL_051.map((s) => (
            <CampoCheckbox
              key={s}
              id={`f051-educacion-${s}`}
              label={t(`${P}.sesiones.${s}`)}
              checked={estado.educacionPrenatal[s] === true}
              onChange={(v) => actualizar({ educacionPrenatal: { ...estado.educacionPrenatal, [s]: v } })}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
